import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';

import { auth } from './firebase-config';
import { authApi } from './services/api';
import { PageLoadingProvider } from './contexts/PageLoadingContext';

import LoginForm from './components/Auth/LoginForm';
import DashboardLayout from './components/Layout/DashboardLayout';
import PipelineDashboard from './components/Pipeline/PipelineDashboard';
import MappingReviewTable from './components/Mapping/MappingReviewTable';
import BrandMappingManager from './components/Brands/BrandMappingManager';
import RelationshipsDashboard from './components/Relationships/RelationshipsDashboard';
import { InvoicingDashboard } from './components/Invoicing';
import { CommissionsDashboard } from './components/Commissions';
import { PerformanceDashboard, ClickPerformanceDashboard, MatchesImpressionsDashboard, BadgeFairnessDashboard, OrderShareDashboard } from './components/Performance';
import { CodeExtractionDashboard } from './components/CodeExtraction';
import RetailersDashboard from './components/Retailers/RetailersDashboard';
import StockVetoesDashboard from './components/StockVetoes/StockVetoesDashboard';
import PartnerRulesOverview from './components/PartnerRules/PartnerRulesOverview';
import PartnerRuleDetail from './components/PartnerRules/PartnerRuleDetail';
import NewPartnerRule from './components/PartnerRules/NewPartnerRule';
import SizeNormalizationDashboard from './components/SizeNormalization/SizeNormalizationDashboard';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#9c27b0',
    },
  },
});

function ProtectedRoute({ children, user, adminUser }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!adminUser) {
    // User is authenticated but not an admin - this shouldn't happen often
    // as the API will reject non-admin users
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Verify user is an admin
          const admin = await authApi.getCurrentUser();
          setAdminUser(admin);
        } catch (err) {
          console.error('Failed to verify admin status:', err);
          setAdminUser(null);
          // Sign out if not authorized
          if (err.message.includes('Not authorized')) {
            auth.signOut();
          }
        }
      } else {
        setAdminUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PageLoadingProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user && adminUser ? (
                <Navigate to="/" replace />
              ) : (
                <LoginForm />
              )
            }
          />

          <Route
            path="/"
            element={<Navigate to="/pipeline" replace />}
          />

          <Route
            path="/pipeline"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <PipelineDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/relationships"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <RelationshipsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoicing"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <InvoicingDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoicing/*"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <InvoicingDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/commissions"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <CommissionsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/commissions/*"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <CommissionsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/performance"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <PerformanceDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/performance/click-to-order"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <ClickPerformanceDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/performance/matches-impressions"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <MatchesImpressionsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/performance/click-balancing"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <BadgeFairnessDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Tab renamed "Badge Fairness" -> "Click balancing" (2026-09-23, Balance Control B4);
              the old path stays as a redirect for bookmarks. */}
          <Route path="/performance/badge-fairness" element={<Navigate to="/performance/click-balancing" replace />} />

          <Route
            path="/performance/order-share"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <OrderShareDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mappings"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <MappingReviewTable />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/brands"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <BrandMappingManager />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/code-extraction"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <CodeExtractionDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/code-extraction/*"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <CodeExtractionDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/retailers"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <RetailersDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner-rules"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <PartnerRulesOverview />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner-rules/new"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <NewPartnerRule />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/partner-rules/:ruleId"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <PartnerRuleDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/stock-vetoes"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <StockVetoesDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/size-normalization"
            element={
              <ProtectedRoute user={user} adminUser={adminUser}>
                <DashboardLayout user={adminUser}>
                  <SizeNormalizationDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Legacy redirects from the old per-page routes */}
          <Route path="/size-aliases" element={<Navigate to="/size-normalization?tab=aliases" replace />} />
          <Route path="/size-schemas" element={<Navigate to="/size-normalization?tab=schemas" replace />} />
          <Route path="/size-curves" element={<Navigate to="/size-normalization?tab=curves" replace />} />
          <Route path="/unknown-sizes" element={<Navigate to="/size-normalization?tab=unknown" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </PageLoadingProvider>
    </ThemeProvider>
  );
}

export default App;
